import { useEffect, useState } from 'react';
import {
  fetchWithdrawalDetail,
  approveWithdrawal,
  rejectWithdrawal,
  completeWithdrawal,
} from '../services/walletService';
import { getBankName } from '../../../utils/bankCode';
import { useAccess } from '../../../context/AccessContext';

const fmt = (n) => (n ?? 0).toLocaleString();

const STATUS_LABEL = {
  REQUESTED: '요청접수', IN_REVIEW: '검토중', APPROVED: '승인',
  REJECTED: '거절', COMPLETED: '완료', CANCELED: '취소',
};
const STATUS_BG = {
  REQUESTED: '#eef2ff', IN_REVIEW: '#fffbeb', APPROVED: '#f0fdf4',
  REJECTED: '#fff1f2', COMPLETED: '#f4f4f5', CANCELED: '#f4f4f5',
};
const STATUS_COLOR = {
  REQUESTED: '#4f46e5', IN_REVIEW: '#d97706', APPROVED: '#16a34a',
  REJECTED: '#be123c', COMPLETED: '#52525b', CANCELED: '#52525b',
};

function formatDeadline(businessDaysLeft, status) {
  if (['REJECTED', 'COMPLETED', 'CANCELED'].includes(status)) return { text: '처리완료', cls: '' };
  if (businessDaysLeft > 1) return { text: `D-${businessDaysLeft}`, cls: '' };
  if (businessDaysLeft === 1) return { text: 'D-1', cls: 'ww-deadline-imminent' };
  if (businessDaysLeft === 0) return { text: 'D-DAY', cls: 'ww-deadline-imminent' };
  return { text: `기한초과 +${Math.abs(businessDaysLeft)}일`, cls: 'ww-deadline-overdue' };
}

function InfoRow({ label, value, highlight, cls }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f4f4f5' }}>
      <span style={{ fontSize: '13px', color: '#6b7280' }}>{label}</span>
      <span className={cls || ''} style={{ fontSize: '14px', fontWeight: 600, color: highlight || '#111827' }}>{value ?? '-'}</span>
    </div>
  );
}

function WithdrawalModal({ isOpen, withdrawalId, onClose, onSuccess }) {
  const { hasPermission } = useAccess();
  const mayApprove = hasPermission('wallet.withdrawal.approve'); // 검토 시작·승인
  const mayReject = hasPermission('wallet.withdrawal.reject'); // 거절
  const mayComplete = hasPermission('wallet.withdrawal.complete'); // 수동 송금 완료
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [restrictedReason, setRestrictedReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || withdrawalId == null) return;
    let cancelled = false;
    setLoading(true); setDetail(null); setError('');
    setRejectReason(''); setRestrictedReason(''); setShowRejectForm(false);
    fetchWithdrawalDetail(withdrawalId)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled) setError(err?.message || '정보를 불러올 수 없습니다.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, withdrawalId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen && !submitting) onClose(); };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  const errMsg = (err) =>
    err?.response?.data?.data?.detail || err?.response?.data?.detail || err?.message || '처리 중 오류가 발생했습니다.';

  function validateAccount() {
    if (!detail?.bankCode || !detail?.accountNumber) {
      setError('계좌 정보가 없어 처리할 수 없습니다. 사용자 계좌 정보를 확인해주세요.');
      return false;
    }
    return true;
  }

  async function handleStartReview() {
    setError('');
    if (!validateAccount()) return;
    setSubmitting(true);
    try { await approveWithdrawal(withdrawalId); onSuccess(); onClose(); }
    catch (err) { setError(errMsg(err)); }
    finally { setSubmitting(false); }
  }

  async function handleApprove() {
    setError('');
    if (!validateAccount()) return;
    if (detail?.riskStatus === 'RESTRICTED' && !restrictedReason.trim()) {
      setError('RESTRICTED 사용자 승인 시 처리 사유 입력이 필요합니다.');
      return;
    }
    setSubmitting(true);
    try {
      await approveWithdrawal(withdrawalId, detail?.riskStatus === 'RESTRICTED' ? restrictedReason : undefined);
      onSuccess(); onClose();
    } catch (err) { setError(errMsg(err)); }
    finally { setSubmitting(false); }
  }

  async function handleRejectConfirm() {
    if (!rejectReason.trim()) { setError('거절 사유를 입력해주세요.'); return; }
    setSubmitting(true); setError('');
    try { await rejectWithdrawal(withdrawalId, rejectReason); onSuccess(); onClose(); }
    catch (err) { setError(errMsg(err)); }
    finally { setSubmitting(false); }
  }

  async function handleComplete() {
    setSubmitting(true); setError('');
    try { await completeWithdrawal(withdrawalId); onSuccess(); onClose(); }
    catch (err) { setError(errMsg(err)); }
    finally { setSubmitting(false); }
  }

  if (!isOpen) return null;

  const deadline = detail ? formatDeadline(detail.businessDaysLeft, detail.status) : null;
  const isRestricted = detail?.riskStatus === 'RESTRICTED';
  // 권한이 없으면 버튼을 렌더하지 않는다 (R-FE-04, 비활성이 아니라 숨김)
  const canAct = mayReject && detail && ['REQUESTED', 'IN_REVIEW', 'APPROVED'].includes(detail.status);
  const canApprove = mayApprove && detail && ['REQUESTED', 'IN_REVIEW'].includes(detail.status);

  const btn = (style) => ({
    height: '40px', padding: '0 20px', fontSize: '14px', fontWeight: 500,
    borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', border: 'none', ...style,
  });

  const textareaStyle = (required) => ({
    width: '100%', padding: '10px 14px', border: `1px solid ${required ? '#fca5a5' : '#e5e7eb'}`,
    borderRadius: '8px', fontSize: '14px', resize: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit', outline: 'none', color: '#111827', lineHeight: 1.6, margin: 0,
  });

  const sectionTitle = (text) => (
    <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '4px', marginTop: '8px', paddingBottom: '6px', borderBottom: '2px solid #e5e7eb' }}>{text}</div>
  );

  return (
    <div className="modal-overlay" data-testid="cancel-modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
      <div
        className="modal-content"
        data-testid="withdrawal-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '520px', width: '92%', padding: 0, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.12)' }}
      >
        {/* 헤더 */}
        <div style={{ padding: '20px 22px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '17px', fontWeight: 700, color: '#0a0a0a', letterSpacing: '-0.3px' }}>출금 요청 상세</span>
            {detail && (
              <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: STATUS_BG[detail.status], color: STATUS_COLOR[detail.status] }}>
                {STATUS_LABEL[detail.status]}
              </span>
            )}
          </div>
          <button className="modal-close-btn" onClick={() => { if (!submitting) onClose(); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '2px 4px' }}>✕</button>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {/* RESTRICTED 경고 */}
          {detail && isRestricted && (
            <div data-testid="restricted-banner" style={{ marginBottom: '18px', padding: '12px 16px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '10px', color: '#be123c', fontSize: '13px', fontWeight: 600 }}>
              ⚠️ 위험 사용자 — 승인 시 처리 사유 필수 입력
            </div>
          )}

          {loading ? (
            <p style={{ textAlign: 'center', padding: '48px 0', color: '#aaa', fontSize: '15px', margin: 0 }}>불러오는 중...</p>
          ) : detail ? (
            <>
              {/* 사용자 정보 */}
              {sectionTitle('사용자 정보')}
              <div style={{ marginBottom: '16px' }}>
                <InfoRow label="예금주" value={detail.accountHolderName || detail.userName || '-'} />
                <InfoRow label="은행" value={getBankName(detail.bankCode)} />
                <InfoRow label="계좌번호" value={detail.accountNumber} />
              </div>

              {/* 출금 정보 */}
              {sectionTitle('출금 정보')}
              <div style={{ marginBottom: '16px' }}>
                <InfoRow label="신청금액" value={`${fmt(detail.requestedAmount)}원`} />
                <InfoRow label="수수료" value={`${fmt(detail.feeAmount)}원`} />
                <InfoRow label="실수령액" value={`${fmt(detail.payoutAmount)}원`} highlight="#4f46e5" />
                <InfoRow label="현재 CP 잔액" value={`${fmt(detail.currentCpBalance)}P`} />
                <InfoRow label="출금 예약 금액" value={`${fmt(detail.reservedCp)}P`} />
                <InfoRow label="출금 가능 CP" value={`${fmt(detail.availableCp)}P`} />
              </div>

              {/* 처리 정보 */}
              {sectionTitle('처리 정보')}
              <div style={{ marginBottom: '16px' }}>
                <InfoRow label="처리기한" value={deadline?.text} cls={deadline?.cls} />
                {detail.completedAt && <InfoRow label="처리 완료일시" value={detail.completedAt.slice(0, 16).replace('T', ' ')} />}
                {detail.rejectReason && (
                  <div style={{ padding: '10px 0', borderBottom: '1px solid #f4f4f5' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>거절 사유</div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#be123c', padding: '8px 12px', background: '#fff1f2', borderRadius: '8px' }}>{detail.rejectReason}</div>
                  </div>
                )}
              </div>

              {/* 거절 사유 입력 */}
              {showRejectForm && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '7px' }}>
                    거절 사유 <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    data-testid="reject-reason-input" value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="거절 사유를 입력해주세요" rows={3}
                    style={textareaStyle(true)}
                  />
                </div>
              )}

              {/* 승인 사유 입력 — RESTRICTED만 필수 표시 */}
              {canApprove && !showRejectForm && isRestricted && (
                <div style={{ marginBottom: '4px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '7px' }}>
                    승인 사유 <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    data-testid="restricted-reason-input" value={restrictedReason}
                    onChange={(e) => setRestrictedReason(e.target.value)}
                    placeholder="승인 처리 사유를 입력해주세요"
                    rows={2} style={textareaStyle(isRestricted)}
                  />
                </div>
              )}
            </>
          ) : null}

          {error && (
            <div data-testid="error-message" style={{ marginTop: '12px', padding: '11px 14px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '8px', color: '#be123c', fontSize: '13px', fontWeight: 500 }}>
              {error}
            </div>
          )}
        </div>

        {/* 하단 버튼: 거절 | 닫기 · 주요액션 */}
        <div style={{ padding: '14px 22px 18px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {canAct && !showRejectForm && (
              <button data-testid="btn-reject" onClick={() => { setShowRejectForm(true); setError(''); }} disabled={submitting}
                style={btn({ background: 'white', color: '#ef4444', border: '1px solid #fca5a5' })}>
                거절
              </button>
            )}
            {mayReject && showRejectForm && (
              <button data-testid="btn-reject-confirm" onClick={handleRejectConfirm} disabled={submitting}
                style={btn({ background: '#fff1f2', color: '#ef4444', border: '1px solid #fca5a5' })}>
                {submitting ? '처리 중...' : '거절 확정'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { if (!submitting) onClose(); }} disabled={submitting}
              style={btn({ background: '#f4f4f5', color: '#52525b' })}>
              {showRejectForm ? '취소' : '닫기'}
            </button>

            {mayApprove && detail?.status === 'REQUESTED' && !showRejectForm && (
              <button data-testid="btn-start-review" onClick={handleStartReview} disabled={submitting}
                style={btn({ background: submitting ? '#e5e7eb' : '#6366f1', color: submitting ? '#9ca3af' : 'white' })}>
                {submitting ? '처리 중...' : '검토 시작'}
              </button>
            )}
            {mayApprove && detail?.status === 'IN_REVIEW' && !showRejectForm && (
              <button data-testid="btn-approve" onClick={handleApprove} disabled={submitting}
                style={btn({ background: submitting ? '#e5e7eb' : '#6366f1', color: submitting ? '#9ca3af' : 'white' })}>
                {submitting ? '처리 중...' : '승인'}
              </button>
            )}
            {mayComplete && detail?.status === 'APPROVED' && !showRejectForm && (
              <button data-testid="btn-complete" onClick={handleComplete} disabled={submitting}
                style={btn({ background: submitting ? '#e5e7eb' : '#15803d', color: submitting ? '#9ca3af' : 'white' })}>
                {submitting ? '처리 중...' : '수동 송금 완료'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WithdrawalModal;
